import { useForm, SubmitHandler, Controller, FieldErrorsImpl, FieldValues } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import Button from '../../../components/buttons/Button'
import BackButton from '../../../components/buttons/BackButton'
import InputField from '../../../components/input-field/InputField'
import FormContainer from '../../../components/page-container/FormContainer'
import FormHeader from '../../../components/page-container/FormHeader'
import FormMain from '../../../components/page-container/FormMain'
import CancleButton from '../../../components/buttons/SecendaryButton'
import { IntegerNumberRegex, PersianCharecterRegex } from '../../../utils/regex'
import { verifyIranianNationalId } from '@persian-tools/persian-tools'
import DatePickerFeild from '../../../components/data-picker-feild/DatePickerFeild'
import convertDateFormat from '../../../utils/convertDateFormat'
import { useAppDispatch, useAppSelector } from '../../../hooks/useStore'
import toastify from '../../../utils/toastify'
import { ToastType } from '../../../types/generalType'
import { setAuthForm } from '../../../store/features/auth/authSlice'
import { AuthForm } from '../../../store/features/auth/type'
import SelectField from '../../../components/select-field/SelectField'
import { setReservePilgrimList } from '../../../store/features/per-reserve/publicPreReserve/publicPreReserveSlice'
import { TPilgrim } from '../../../store/features/per-reserve/publicPreReserve/types'
import checkHasLeader from '../../../utils/checkHasLeader'

type IForms = Omit<TPilgrim, 'isLeader' | 'sajayaPrice' | 'hasSajaya'> & {
  leader: string | undefined
}
const schema: yup.SchemaOf<IForms> = yup.object({
  firstName: yup
    .string()
    .matches(PersianCharecterRegex, {
      message: 'persianCharacter',
      excludeEmptyString: true,
    })
    .required('requiedFeild'),
  lastName: yup
    .string()
    .matches(PersianCharecterRegex, {
      message: 'persianCharacter',
      excludeEmptyString: true,
    })
    .required('requiedFeild'),
  fatherName: yup
    .string()
    .matches(PersianCharecterRegex, {
      message: 'persianCharacter',
      excludeEmptyString: true,
    })
    .required('requiedFeild'),
  birthDate: yup.string().required('requiedFeild'),
  nationalCode: yup
    .string()
    .matches(IntegerNumberRegex, {
      message: 'integerNumber',
      excludeEmptyString: true,
    })
    .min(10, 'nationalCodeLength')
    .max(10, 'nationalCodeLength')
    .required('requiedFeild')
    .test('verifyNationaCode', 'notValidNationalCode', (value) => {
      return verifyIranianNationalId(value) as boolean
    }),
  martyrNumber: yup
    .string()
    .matches(IntegerNumberRegex, {
      message: 'integerNumber',
      excludeEmptyString: true,
    })
    .required('requiedFeild'),
  leader: yup.string().required('requiedFeild'),
})

const PublicAddPilgrim = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IForms>({
    resolver: yupResolver(schema),
  })

  const { pilgrims } = useAppSelector((state) => state.publicPreReserve.preRserve)
  const leader = checkHasLeader(pilgrims)
  const selectOptions = leader
    ? [{ label: 'همراه', value: '1' }]
    : [
        { label: 'سرپرست', value: '0' },
        { label: 'همراه', value: '1' },
      ]

  const handleGoBackClick = () => {
    dispatch(setAuthForm(AuthForm.PublicPreReserve))
  }

  const handleSubmitForm: SubmitHandler<IForms> = (pilgrimData) => {
    const isLeader = pilgrimData.leader === '0' ? true : false

    delete pilgrimData.leader

    const data = {
      ...pilgrimData,
      birthDate: convertDateFormat(pilgrimData.birthDate),
      isLeader,
      hasSajaya: false,
      sajayaPrice: 0,
    }

    const pilgrimList = [...pilgrims]

    const pilgrimIndex = pilgrimList.findIndex(
      (pilgrim) => pilgrim.nationalCode === data.nationalCode,
    )
    if (pilgrimIndex !== -1)
      return toastify(ToastType.ERROR, {
        toastMessage: t('pilgrimIsInList'),
      })

    pilgrimList.push(data)

    dispatch(setReservePilgrimList(pilgrimList))

    toastify(ToastType.SUCCESS, {
      toastMessage: t('pilgrimAddToList'),
    })

    dispatch(setAuthForm(AuthForm.PublicPreReserve))
  }

  return (
    <div className='bg-neutral-50'>
      <div className='w-4/5 h-screen container mx-auto pt-8'>
        <FormContainer>
          <FormHeader title='addNewPilgrim'>
            <BackButton onClick={handleGoBackClick} />
          </FormHeader>
          <FormMain>
            <div className='flex flex-col w-full h-full justify-between'>
              <form className='w-full h-full mb-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 items-center justify-between'>
                <InputField
                  id={'martyrNumber'}
                  lableTitle={t('martyrNumber')}
                  errors={errors as FieldErrorsImpl<FieldValues>}
                  {...register('martyrNumber')}
                />
                <InputField
                  id={'nationalCode'}
                  lableTitle={t('nationalCode')}
                  errors={errors as FieldErrorsImpl<FieldValues>}
                  {...register('nationalCode')}
                />

                <Controller
                  name='leader'
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field, formState }) => {
                    return (
                      <SelectField
                        field={field}
                        formState={formState}
                        lableTitle={t('title')}
                        options={selectOptions}
                      />
                    )
                  }}
                />
                <InputField
                  classes='md:col-start-1 md:col-strat-2'
                  id={'firstName'}
                  lableTitle={t('firstName')}
                  errors={errors as FieldErrorsImpl<FieldValues>}
                  {...register('firstName')}
                />
                <InputField
                  id={'lastName'}
                  lableTitle={t('lastName')}
                  errors={errors as FieldErrorsImpl<FieldValues>}
                  {...register('lastName')}
                />
                <InputField
                  id={'fatherName'}
                  lableTitle={t('fatherName')}
                  errors={errors as FieldErrorsImpl<FieldValues>}
                  {...register('fatherName')}
                />
                <Controller
                  name='birthDate'
                  control={control}
                  render={({ field, formState }) => (
                    <DatePickerFeild
                      maxDate={true}
                      field={field}
                      formState={formState}
                      lableTitle={t('brithday')}
                    />
                  )}
                />
              </form>
              <div className='md:mt-6 flex items-center justify-end'>
                <CancleButton onClick={handleGoBackClick}>{t('cancle')}</CancleButton>
                <Button
                  types='success'
                  classes='px-12 mr-2'
                  onClick={handleSubmit(handleSubmitForm)}
                >
                  {t('add')}
                </Button>
              </div>
            </div>
          </FormMain>
        </FormContainer>
      </div>
    </div>
  )
}

export default PublicAddPilgrim
